// Players list

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFirebase } from './FirebaseContextProvider';
import { UsersRoles, ParentStatuses } from '../utils/enums';
import { capFirstLetter, isTooOld } from '../utils/misc';
import GuidedTour from './GuidedTour';
import Tooltip from './Tooltip';
import { useModal } from './ModalProvider';
import Spinner from './Spinner';
import { userRemoveTeam } from '../utils/firebase';
import FilterInput from './FilterInput';
import InviteButton from './InviteButton';
import PlayerStatus from './PlayerStatus';
import MarkedText from './MarkedText';
import { useWaiverStatuses, useTeamRoster, useEntities } from '../utils/hooks';
import Avatar from './Avatar';
import './PlayersList.scss';

const rosterSteps = [
  {
    target: '.invite-player',
    title: 'Invite players',
    content: 'Click here to invite players to your team',
  },
  {
    target: '.add-player',
    title: 'Add players',
    content: 'Click here to manually add players to your team',
  },
];

const playerSteps = [
  {
    target: '.player:first-of-type',
    title: 'Player',
    content: 'Here is your player. Click to view details',
  },
];

export function ParentStatus({ status }) {
  if (status === ParentStatuses.APPROVED)
    return <MarkedText text='Parent confirmed' mark='check' />;
  if (status === ParentStatuses.PENDING)
    return <MarkedText text='Pending approval' mark='clock' />;
  if (status === ParentStatuses.REJECTED)
    return <MarkedText text='Parent rejected' mark='cross' />;
  return <MarkedText text='No parent' mark='cross' />;
}

export default function PlayersList({ teamId, userId }) {
  const [run, setRun] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const [removePlayerId, setRemovePlayerId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  const {
    user,
    teamsById,
    userIsCoach,
    userIsAdmin,
    userIsParent,
    childrenByParentId,
    getParentStatus,
  } = useFirebase();
  const { openModal } = useModal();

  // Two modes, two ways in. A parent's children are a known set of ids, so
  // they are read one by one through the entity registry; a team's roster is a
  // list query, narrowed as far as the rules let this caller prove — see
  // `useTeamRoster`.
  const childIds = useMemo(
    () =>
      userId
        ? (childrenByParentId.get(userId) ?? []).map((child) => child.id)
        : [],
    [userId, childrenByParentId],
  );
  const { entities: children, isResolved: areChildrenResolved } = useEntities(
    'users',
    childIds,
  );
  const { players: teamPlayers, isLoaded: areTeamPlayersLoaded } =
    useTeamRoster(teamId);
  // The team's own document, for the age it checks a player against. Held by
  // the card around this list as well, and the registry is ref-counted — so
  // this costs nothing and does not depend on that card staying as it is.
  const { isResolved: isTeamResolved } = useEntities(
    'teams',
    teamId ? [teamId] : [],
  );
  // The roster arrives with the tab now, not with the session — an empty list
  // in the meantime reads as "no players on this team". The team document is
  // waited on too: the age check below reads as "not too old" without it.
  const isRosterReady = teamId
    ? areTeamPlayersLoaded && isTeamResolved
    : areChildrenResolved;

  const isTeamCoach = teamId && userIsCoach && user.teams?.includes(teamId);
  const canManageRoster = teamId && (userIsAdmin || isTeamCoach);
  const parentId = userId ?? (userIsParent ? user.id : undefined);
  const showStatus = userId || canManageRoster;

  const waiverParams = teamId
    ? { teamId }
    : userId
      ? { parentId: userId }
      : null;
  const { waiverMap } = useWaiverStatuses(waiverParams);

  // Memoize filtered, sorted and preprocessed player data
  const players = useMemo(() => {
    const source = teamId
      ? teamPlayers
      : userId
        ? children.filter((u) => u && u.role === UsersRoles.PLAYER)
        : [];

    // Copied before sorting: `teamPlayers` is the array `useTeamRoster`
    // memoizes, and sorting in place would reorder it behind the memo
    return [...source]
      .sort((a, b) =>
        a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }),
      )
      .map((u) => {
        const { id, name, imgThumbPath, birthDate, gender, card, verified } = u;
        const { expireDate: cardExp, number: cardNum } = card ?? {};
        const genderCapitalized = capFirstLetter(gender);
        const birthDateFormatted = birthDate
          ? birthDate.format('MMM D, YY')
          : '';
        let cardInfoString = null;
        let hasCard = false;
        if (cardNum && cardExp) {
          hasCard = true;
          cardInfoString = `#${cardNum} exp.\u00A0${cardExp.format(
            'MMM\u00A0DD,\u00A0YY',
          )}`;
        }
        const parentStatus = parentId
          ? getParentStatus(u.id, parentId)
          : undefined;
        const isPlayerCoach = teamId
          ? isTeamCoach
          : userIsCoach && u.teams?.some((t) => user.teams?.includes(t));
        const disabled =
          !(userIsAdmin || isPlayerCoach) &&
          id !== user.id &&
          parentStatus !== ParentStatuses.APPROVED;
        const teamObj = teamId ? teamsById.get(teamId) : null;
        const tooOld = teamObj
          ? isTooOld({
              birthDate: u.birthDate,
              age: teamObj.age,
              cutOffDate: teamObj.cutOffDate,
            })
          : false;
        return {
          id,
          name,
          imgThumbPath,
          genderCapitalized,
          birthDateFormatted,
          hasCard,
          cardInfoString,
          verified,
          parentStatus,
          disabled,
          tooOld,
        };
      });
  }, [
    teamPlayers,
    children,
    teamId,
    userId,
    parentId,
    getParentStatus,
    userIsCoach,
    userIsAdmin,
    user.teams,
    user.id,
    isTeamCoach,
    teamsById,
  ]);

  // Every target must be rendered: the toolbar steps carry the same condition
  // as the buttons, the row step needs a player
  const tourSteps = useMemo(
    () =>
      userIsCoach
        ? [
            ...(canManageRoster ? rosterSteps : []),
            ...(players.length > 0 ? playerSteps : []),
          ]
        : [],
    [userIsCoach, canManageRoster, players.length],
  );

  // One key per step set: a run that showed fewer steps must not mark a fuller
  // set seen. The row step waits for a player, the toolbar steps exist only for
  // the roster's own coach
  const tourKey = !canManageRoster
    ? 'players-list-v1'
    : players.length > 0
      ? 'players-roster-v1'
      : 'players-roster-toolbar-v1';

  // A coach with an empty roster still gets the toolbar steps, so this waits
  // for the roster to settle rather than for a player to show up
  useEffect(() => {
    if (isRosterReady) setRun(true);
  }, [isRosterReady]);

  // Stop spinner and reset removePlayerId if the target player is no longer in the players list
  useEffect(() => {
    if (!spinner || !removePlayerId) return;
    if (!players.some((p) => p.id === removePlayerId)) {
      setSpinner(false);
      setRemovePlayerId(null);
    }
  }, [spinner, players, removePlayerId]);

  const searchValues = useMemo(
    () => players.map((p) => [p.name, 'DOB: ' + p.birthDateFormatted]),
    [players],
  );

  const displayedPlayers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return players;
    return players.filter((_, i) =>
      searchValues[i].some((val) => val && val.toLowerCase().includes(q)),
    );
  }, [players, searchValues, searchQuery]);

  // Remove the selected player from the current team
  const handlePlayerRemove = (e, playerId) => {
    e.preventDefault();

    const playerName = players.find((p) => p.id === playerId)?.name;
    const teamName = teamsById.get(teamId)?.name;

    const onRemove = async () => {
      try {
        setSpinner(true);
        setRemovePlayerId(playerId);
        await userRemoveTeam({
          id: playerId,
          team: teamId,
        });
      } catch (error) {
        setSpinner(false);
        toast.error(error?.message ?? 'Failed to remove player');
      }
    };

    openModal({
      header: `Remove player from team`,
      body: (
        <p className='font-16'>
          {`Are you sure you want to remove `}
          <span className='font-medium'>{playerName}</span>
          {` from `}
          <span className='font-medium'>{teamName}?</span>
        </p>
      ),
      buttons: [
        {
          text: 'Cancel',
          className: 'bw',
        },
        {
          text: 'Remove',
          className: 'danger',
          onClick: onRemove,
        },
      ],
    });
  };

  return (
    <GuidedTour steps={tourSteps} run={run} tourKey={tourKey}>
      <div className='players-list'>
        <div className='sticky-top'>
          <div className='controls'>
            <div className='filters'>
              <FilterInput
                className='search'
                placeholder='Search'
                value={searchQuery}
                onChange={setSearchQuery}
                small
              />
            </div>
            {canManageRoster && (
              <div className='actions'>
                <InviteButton
                  className='invite-player'
                  tokenCreatePayload={{ team: teamId, role: UsersRoles.PLAYER }}
                  invited='players'
                >
                  Invite players
                </InviteButton>
                <Link
                  to={`/players/new?edit=true&teamid=${teamId}`}
                  className='button small bw add-player'
                >
                  Add player
                </Link>
              </div>
            )}
          </div>
          {!(searchQuery.trim() && displayedPlayers.length === 0) && (
            <ul className='header tab-header'>
              <li className='col-1' />
              <li className='col-2'>Name</li>
              <li className='col-3'>Gender, Birth&nbsp;date</li>
              <li className='col-23'>Name, Gender, Birth&nbsp;date</li>
              <li className='col-234'>Name, Gender, Birth&nbsp;date, Card</li>
              {showStatus && <li className='col-4'>Status</li>}
              <li className='col-5'>Card</li>
              {canManageRoster && <li className='col-6'>Delete</li>}
            </ul>
          )}
        </div>
        {!isRosterReady && <Spinner show inline />}
        {searchQuery.trim() && displayedPlayers.length === 0 && (
          <h5 className='name'>No results found for your search</h5>
        )}
        {displayedPlayers.map((player) => {
          const {
            id,
            name,
            genderCapitalized,
            birthDateFormatted,
            hasCard,
            cardInfoString,
            verified,
            parentStatus,
          } = player;

          return (
            <Link
              to={'/players/' + id}
              className={'player' + (player.disabled ? ' disabled' : '')}
              key={id}
              onClick={player.disabled ? (e) => e.preventDefault() : undefined}
            >
              <Avatar user={player} size={64} className='col-1' />
              <div className='col-2'>
                <p className='font-medium upper'>{name}</p>
              </div>
              <div className='col-3'>
                <p>{genderCapitalized}</p>
                <p>{birthDateFormatted}</p>
              </div>
              <div className='col-23'>
                <p className='font-medium upper'>{name}</p>
                <p>{genderCapitalized}</p>
                <p>{birthDateFormatted}</p>
              </div>
              <div className='col-234'>
                <div className='img-name-wrap'>
                  <Avatar user={player} size={32} />
                  <p className='font-medium upper'>{name}</p>
                </div>
                <p>{genderCapitalized}</p>
                <p>{birthDateFormatted}</p>
                <p>
                  {hasCard ? (
                    cardInfoString
                  ) : (
                    <span className='danger upper font-medium'>No card</span>
                  )}
                </p>
              </div>
              {showStatus && (
                <div className='col-4'>
                  {!userId && (
                    <PlayerStatus
                      verified={verified ?? false}
                      waiverSigned={waiverMap.get(id)}
                      tooOld={player.tooOld}
                    />
                  )}
                  {userId && parentStatus !== ParentStatuses.APPROVED && (
                    <ParentStatus status={parentStatus} />
                  )}
                  {userId && parentStatus === ParentStatuses.APPROVED && (
                    <PlayerStatus
                      verified={verified ?? false}
                      waiverSigned={waiverMap.get(id)}
                    />
                  )}
                </div>
              )}
              <div className='col-5'>
                <p>
                  {hasCard ? (
                    cardInfoString
                  ) : (
                    <span className='danger upper font-medium'>No card</span>
                  )}
                </p>
              </div>
              {canManageRoster && (
                <div className='col-6'>
                  <Tooltip label='Remove player from the team'>
                    <button
                      className='transparent small'
                      onClick={(e) => handlePlayerRemove(e, id)}
                      aria-label='Remove player from the team'
                    >
                      <p className='fa-trash danger' />
                    </button>
                  </Tooltip>
                </div>
              )}
            </Link>
          );
        })}
        <Spinner show={spinner} />
      </div>
    </GuidedTour>
  );
}
